import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
  Modal, FormRow, TextInput, TextArea, RiskLevelSelect,
  FormError, SubmitBar,
} from './admin-ui';

/**
 * Slice 7.6 — generic entity editor for the 6 simpler threat-catalog tables.
 *
 * Configurable via the `kind` prop. Each kind defines which fields to
 * render and how to map state to the API payload. Keeps us out of writing
 * 6 near-identical form components.
 *
 * Composition editing is its own thing (see CompositionEditor) because the
 * dropdown wiring and source selection are too specific to the composition
 * shape to share useful code with these.
 */

export type EntityKind =
  | 'sources/adversarial'
  | 'sources/non-adversarial'
  | 'events'
  | 'vulnerabilities'
  | 'predisposing-conditions'
  | 'impacts';

interface FieldDef {
  name: string;
  label: string;
  kind: 'text' | 'textarea' | 'risk_level';
  required?: boolean;
  hint?: string;
  maxLength?: number;
}

interface EntityDef {
  title: string;
  invalidateKey: string[];
  fields: FieldDef[];
}

const ENTITY_DEFS: Record<EntityKind, EntityDef> = {
  'sources/adversarial': {
    title: 'Adversarial source',
    invalidateKey: ['threat-catalog', 'sources-adv'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64, hint: 'Optional reference (e.g. TSA999).' },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'threat_type', label: 'Type', kind: 'text', required: true, maxLength: 128, hint: "e.g. 'Individual - Insider', 'Group - Established', 'Nation-State'." },
      { name: 'description', label: 'Description', kind: 'textarea' },
      { name: 'capability', label: 'Capability', kind: 'risk_level', required: true },
      { name: 'intent', label: 'Intent', kind: 'risk_level', required: true },
      { name: 'targeting', label: 'Targeting', kind: 'risk_level', required: true },
      { name: 'affiliation', label: 'Affiliation', kind: 'text', maxLength: 255 },
      { name: 'geographic_location', label: 'Geo. location', kind: 'text', maxLength: 255 },
      { name: 'threat_level', label: 'Threat level', kind: 'risk_level', required: true },
    ],
  },
  'sources/non-adversarial': {
    title: 'Non-adversarial source',
    invalidateKey: ['threat-catalog', 'sources-non-adv'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64 },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'threat_type', label: 'Type', kind: 'text', required: true, maxLength: 128, hint: "e.g. 'Natural', 'Equipment - Failure', 'Human - Error'." },
      { name: 'description', label: 'Description', kind: 'textarea' },
      { name: 'range_of_effects', label: 'Range of effects', kind: 'risk_level', required: true },
      { name: 'affiliation', label: 'Affiliation', kind: 'text', maxLength: 255 },
      { name: 'geographic_location', label: 'Geo. location', kind: 'text', maxLength: 255 },
      { name: 'threat_level', label: 'Threat level', kind: 'risk_level', required: true },
    ],
  },
  events: {
    title: 'Threat event',
    invalidateKey: ['threat-catalog', 'events'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64 },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'type', label: 'Type', kind: 'text', required: true, maxLength: 128, hint: "e.g. 'Adversarial', 'Non_adversarial'." },
      { name: 'sub_type', label: 'Sub-type', kind: 'text', maxLength: 128 },
      { name: 'description', label: 'Description', kind: 'textarea' },
      { name: 'event_relevance', label: 'Relevance', kind: 'text', required: true, maxLength: 64, hint: "e.g. 'Confirmed', 'Possible', 'Predicted'." },
      { name: 'timeline', label: 'Timeline', kind: 'text', maxLength: 255 },
      { name: 'mitigating_factors', label: 'Mitigations', kind: 'textarea' },
    ],
  },
  vulnerabilities: {
    title: 'Vulnerability',
    invalidateKey: ['threat-catalog', 'vulnerabilities'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64 },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'type', label: 'Type', kind: 'text', required: true, maxLength: 128 },
      { name: 'description', label: 'Description', kind: 'textarea' },
      { name: 'severity', label: 'Severity', kind: 'risk_level', required: true },
    ],
  },
  'predisposing-conditions': {
    title: 'Predisposing condition',
    invalidateKey: ['threat-catalog', 'predispos'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64 },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'type', label: 'Type', kind: 'text', required: true, maxLength: 128 },
      { name: 'description', label: 'Description', kind: 'textarea' },
      { name: 'pervasiveness', label: 'Pervasiveness', kind: 'risk_level', required: true },
    ],
  },
  impacts: {
    title: 'Impact',
    invalidateKey: ['threat-catalog', 'impacts'],
    fields: [
      { name: 'legacy_code', label: 'Legacy code', kind: 'text', maxLength: 64 },
      { name: 'name', label: 'Name', kind: 'text', required: true, maxLength: 255 },
      { name: 'type', label: 'Type', kind: 'text', required: true, maxLength: 128, hint: "e.g. 'Confidentiality', 'Integrity', 'Availability'." },
      { name: 'description', label: 'Description', kind: 'textarea' },
    ],
  },
};

export function EntityEditor({ kind, open, onClose, existing }: {
  kind: EntityKind;
  open: boolean;
  onClose: () => void;
  existing?: { id: string } & Record<string, unknown>;
}) {
  const def = ENTITY_DEFS[kind];
  const isEdit = !!existing;
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init: Record<string, string> = {};
    for (const f of def.fields) {
      const v = existing ? (existing[f.name] ?? '') : '';
      init[f.name] = v == null ? '' : String(v);
    }
    setForm(init);
    setError(null);
  }, [open, existing, def]);

  function patch(name: string, value: string) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleSubmit() {
    setError(null);

    // Local validation
    for (const f of def.fields) {
      if (f.required && !form[f.name]?.toString().trim()) {
        setError(`${f.label} is required.`);
        return;
      }
    }

    // Build payload — empty strings for non-required text fields become null
    const payload: Record<string, string | null> = {};
    for (const f of def.fields) {
      const v = form[f.name]?.trim() ?? '';
      payload[f.name] = v.length === 0 && !f.required ? null : v;
    }

    setSubmitting(true);
    try {
      if (isEdit && existing) {
        await api.put(`/admin/threat-catalog/${kind}/${existing.id}`, payload);
      } else {
        await api.post(`/admin/threat-catalog/${kind}`, payload);
      }
      qc.invalidateQueries({ queryKey: def.invalidateKey });
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${def.title.toLowerCase()}` : `New ${def.title.toLowerCase()}`}
    >
      <FormError message={error} />

      {def.fields.map((f) => (
        <FormRow key={f.name} label={f.label} hint={f.hint}>
          {f.kind === 'text' && (
            <TextInput
              value={form[f.name] ?? ''}
              onChange={(v) => patch(f.name, v)}
              required={f.required}
              maxLength={f.maxLength}
            />
          )}
          {f.kind === 'textarea' && (
            <TextArea
              value={form[f.name] ?? ''}
              onChange={(v) => patch(f.name, v)}
            />
          )}
          {f.kind === 'risk_level' && (
            <RiskLevelSelect
              value={form[f.name] ?? ''}
              onChange={(v) => patch(f.name, v)}
              required={f.required}
            />
          )}
        </FormRow>
      ))}

      <SubmitBar
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? 'Save changes' : 'Create'}
      />
    </Modal>
  );
}
